import React, { useState, useEffect } from "react";
import {
  TextInput,
  Textarea,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
  Group,
  Image,
  Paper,
  FileInput,
  Loader,
  Title,
} from "@mantine/core";
import { AppModal } from "../../../generic/AppModal";
import { Venue, VenueInput } from "../../../../utils/api/VenueService";
import { AppIcon } from "../../../generic/AppIcon";
import { AppButton } from "../../../generic/AppButton";
import { StatusSelector } from "../../StatusSelector";
import { useUploadedFiles } from "../../../../hooks/useUploadedFiles";
import {
  resizeImageIfNeeded,
  validateImageFile,
  getSupportedImageTypesString,
  getSupportedFormatsDescription,
  createResizeNote,
  createUploadNotes,
  DEFAULT_IMAGE_RESIZE_CONFIG,
  type ImageResizeConfig,
} from "../../../../utils/imageUtils";
import { AddressAutocompleteInput } from "../../../generic/AddressAutocompleteInput";
import { AddressData } from "../../../../hooks/useGoogleMapsAutocomplete";

export interface VenueFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Venue to edit (if null/undefined, it's create mode) */
  venue?: Venue | null;

  /** Callback when venue should be created */
  onCreateVenue?: (venueData: VenueInput) => Promise<Venue | null>;

  /** Callback when venue should be updated */
  onUpdateVenue?: (
    venueIs: string,
    venueData: VenueInput,
  ) => Promise<Venue | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from venue operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface VenueFormData {
  name: string | undefined;
  description: string | undefined;
  address1: string | undefined;
  address2: string | undefined;
  city: string | undefined;
  stateProvince: string | undefined;
  postalCode: string | undefined;
  country: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  website: string | undefined;
  contactPerson: string | undefined;
  logoUrl: string | undefined;
  uploadedFileId: string | null | undefined;
  status: number;
}

const initialFormData: VenueFormData = {
  name: "",
  description: "",
  address1: "",
  address2: "",
  city: "",
  stateProvince: "",
  postalCode: "",
  country: "",
  phone: "",
  email: "",
  website: "",
  contactPerson: "",
  logoUrl: "",
  uploadedFileId: null,
  status: 100,
};

export const VenueFormModal: React.FC<VenueFormModalProps> = ({
  opened,
  onClose,
  venue,
  onCreateVenue,
  onUpdateVenue,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<VenueFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [resizeConfig] = useState<ImageResizeConfig>(
    DEFAULT_IMAGE_RESIZE_CONFIG,
  );
  const [resizeNote, setResizeNote] = useState<string>("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingImage, setIsDeletingImage] = useState(false);

  const uploadedFilesHook = useUploadedFiles();

  const isEditMode = Boolean(venue);
  const isSubmitting = isCreating || isUpdating;
  const hasLogo = Boolean(formData.logoUrl);

  // Reset form when modal opens/closes or venue changes
  useEffect(() => {
    if (opened) {
      if (venue) {
        // Edit mode - populate form with venue data
        setFormData({
          name: venue.name,
          description: venue.description,
          address1: venue.address1,
          address2: venue.address2,
          city: venue.city,
          stateProvince: venue.stateProvince,
          postalCode: venue.postalCode,
          country: venue.country,
          phone: venue.phone,
          email: venue.email,
          website: venue.website,
          contactPerson: venue.contactPerson,
          logoUrl: venue.logoUrl,
          uploadedFileId: venue.uploadedFileId,
          status: venue.status != null ? venue.status : 100,
        });
      } else {
        // Create mode - reset to initial values
        setFormData(initialFormData);
      }
      setValidationErrors({});
      setResizeNote("");
      onClearError?.();
    }
  }, [opened, venue, onClearError]);

  const validateForm = (): boolean => {
    const newErrors: Partial<any> = {};

    // Required fields
    if (!formData.name?.trim()) {
      newErrors.name = "Venue name is required";
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Website validation
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = "Website must start with http:// or https://";
    }

    setValidationErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof VenueFormData,
    value: string | number | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: ["status"].includes(field) ? Number(value) : value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle address selection from autocomplete
  const handleAddressChange = (
    address1: string,
    fullAddress: AddressData | null,
  ) => {
    if (fullAddress) {
      // Populate all address fields from the full address object
      setFormData((prev) => ({
        ...prev,
        address1: fullAddress.address1 || address1,
        address2: fullAddress.address2 || prev.address2,
        city: fullAddress.city || prev.city,
        stateProvince: fullAddress.region || prev.stateProvince,
        postalCode: fullAddress.postalCode || prev.postalCode,
        country: fullAddress.country || prev.country,
      }));
    } else {
      // Only update address1 if no full address is available
      setFormData((prev) => ({
        ...prev,
        address1: address1,
      }));
    }

    // Clear validation error for address1
    if (validationErrors.address1) {
      setValidationErrors((prev: any) => ({
        ...prev,
        address1: undefined,
      }));
    }
  };

  // Handle file selection and upload
  const handleFileSelect = async (file: File | null) => {
    if (!file) return;

    // Validate file type using utils
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      setValidationErrors((prev: any) => ({
        ...prev,
        logoUrl: validation.error || "",
      }));
      return;
    }

    setIsUploadingImage(true);
    setResizeNote("");

    try {
      // Resize image if needed using utils
      const resizeResult = await resizeImageIfNeeded(file, resizeConfig);

      if (
        resizeResult.wasResized &&
        resizeResult.newDimensions &&
        resizeResult.originalDimensions
      ) {
        setResizeNote(
          createResizeNote(
            resizeResult.originalDimensions,
            resizeResult.newDimensions,
          ),
        );
      }

      // Upload the file
      const uploadResult = await uploadedFilesHook.create({
        name: resizeResult.file.name,
        originalFilename: file.name,
        mimeType: resizeResult.file.type,
        notes: createUploadNotes(
          resizeResult.wasResized,
          resizeResult.originalDimensions,
          resizeResult.newDimensions,
        ),
        files: [resizeResult.file],
      });

      if (uploadResult) {
        // Update form data with uploaded file info
        setFormData((prev) => ({
          ...prev,
          logoUrl: uploadResult.fileUrl || "",
          uploadedFileId: uploadResult.id || "",
        }));

        // Clear any previous errors
        setValidationErrors((prev: any) => ({
          ...prev,
          logoUrl: "",
        }));
      } else {
        setValidationErrors((prev: any) => ({
          ...prev,
          logoUrl: uploadedFilesHook.lastError || "Failed to upload image",
        }));
      }
    } catch (error) {
      setValidationErrors((prev: any) => ({
        ...prev,
        logoUrl:
          error instanceof Error ? error.message : "Failed to process image",
      }));
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Handle image removal
  const handleRemoveImage = async () => {
    if (!formData.uploadedFileId) return;

    setIsDeletingImage(true);

    try {
      const success = await uploadedFilesHook.remove({
        id: formData.uploadedFileId,
      });

      if (success) {
        setFormData((prev) => ({
          ...prev,
          logoUrl: "",
          uploadedFileId: null,
        }));
        setResizeNote("");
      } else {
        // setValidationErrors((prev) => ({
        //   ...prev,
        //   logoUrl: uploadedFilesHook.lastError || "Failed to remove image",
        // }));
        setFormData((prev) => ({
          ...prev,
          logoUrl: "",
          uploadedFileId: null,
        }));
      }
    } catch (error: any) {
      // setValidationErrors((prev) => ({
      //   ...prev,
      //   logoUrl:
      //     error instanceof Error ? error.message : "Failed to remove image",
      // }));
      setFormData((prev) => ({
        ...prev,
        logoUrl: "",
        uploadedFileId: null,
      }));
    } finally {
      setIsDeletingImage(false);
    }
  };

  // Get the error to display for a field
  const getDisplayError = (
    field: keyof VenueFormData,
  ): string | null | undefined => {
    return validationErrors[field];
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare venue data
      const venueData: VenueInput = {
        ...formData,
        // Convert empty strings to undefined for optional fields
        description: formData.description,
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        stateProvince: formData.stateProvince,
        postalCode: formData.postalCode,
        country: formData.country,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        contactPerson: formData.contactPerson,
        logoUrl: formData.logoUrl,
        uploadedFileId: formData.uploadedFileId,
      };

      let result: Venue | null = null;

      if (isEditMode && venue && onUpdateVenue) {
        // Update existing venue
        result = await onUpdateVenue(venue.id || "", venueData);
      } else if (!isEditMode && onCreateVenue) {
        // Create new venue
        result = await onCreateVenue(venueData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render logo upload section
  const renderLogoUpload = () => {
    return (
      <Stack gap="sm">
        <InputLabel mb="xs">Logo</InputLabel>

        {hasLogo ? (
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Current Logo
                  </Text>
                  <Image
                    src={formData.logoUrl}
                    alt="Venue logo"
                    w={200}
                    h={150}
                    fit="contain"
                    fallbackSrc="/placeholder-image.png"
                  />
                </Stack>
                <AppButton
                  variant="danger"
                  size="sm"
                  iconName="Trash"
                  onClick={handleRemoveImage}
                  inProgress={isDeletingImage}
                  disabled={isUploadingImage || isSubmitting}
                >
                  Remove
                </AppButton>
              </Group>

              {resizeNote && (
                <Alert
                  color="blue"
                  icon={<AppIcon iconName="InfoCircle" size={16} />}
                >
                  <Text size="sm">{resizeNote}</Text>
                </Alert>
              )}
            </Stack>
          </Paper>
        ) : (
          <Stack gap="sm">
            <FileInput
              placeholder="Choose image file"
              accept={getSupportedImageTypesString()}
              onChange={handleFileSelect}
              disabled={isUploadingImage || isSubmitting}
              leftSection={
                isUploadingImage ? (
                  <Loader size={16} />
                ) : (
                  <AppIcon iconName="Upload" size={16} />
                )
              }
              error={getDisplayError("logoUrl")}
              size="md"
            />

            <Text size="xs" c="dimmed">
              Supported formats: {getSupportedFormatsDescription()}. Images
              larger than {resizeConfig.maxWidth}x{resizeConfig.maxHeight} will
              be automatically resized.
            </Text>
          </Stack>
        )}
      </Stack>
    );
  };

  // Render generic errors at the bottom of the form
  const renderGenericErrors = () => {
    if (!lastError && !uploadedFilesHook.lastError) {
      return null;
    }

    const errorMessage = lastError || uploadedFilesHook.lastError;

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{errorMessage}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Venue" : "Add New Venue"}
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting || isUploadingImage,
        },
        {
          label: "Confirm",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
          disabled: isUploadingImage,
        },
      ]}
    >
      <Stack gap="md">
        {/* Basic Information */}
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Venue Name"
              placeholder="Enter venue name"
              value={formData.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={getDisplayError("name")}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Description"
              placeholder="Enter venue description"
              value={formData.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              error={getDisplayError("description")}
              autosize
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Logo Upload */}
        <Grid>
          <Grid.Col span={12}>{renderLogoUpload()}</Grid.Col>
        </Grid>

        {/* Address Information */}
        <Grid>
          <Grid.Col span={12}>
            <Title order={5} mb="sm">
              Address
            </Title>
          </Grid.Col>

          <Grid.Col span={12}>
            <AddressAutocompleteInput
              label="Address Line 1"
              placeholder="Start typing to search for an address"
              value={formData.address1 || ""}
              onChange={handleAddressChange}
              error={getDisplayError("address1")}
              disabled={isSubmitting}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <TextInput
              label="Address Line 2"
              placeholder="Apartment, suite, etc. (optional)"
              value={formData.address2}
              onChange={(e) => handleInputChange("address2", e.target.value)}
              error={getDisplayError("address2")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="City"
              placeholder="Enter city"
              value={formData.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
              error={getDisplayError("city")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="State/Province"
              placeholder="Enter state or province"
              value={formData.stateProvince}
              onChange={(e) =>
                handleInputChange("stateProvince", e.target.value)
              }
              error={getDisplayError("stateProvince")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Postal Code"
              placeholder="Enter postal code"
              value={formData.postalCode}
              onChange={(e) => handleInputChange("postalCode", e.target.value)}
              error={getDisplayError("postalCode")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Country"
              placeholder="Enter country"
              value={formData.country}
              onChange={(e) => handleInputChange("country", e.target.value)}
              error={getDisplayError("country")}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Contact Information */}
        <Grid>
          <Grid.Col span={12}>
            <Title order={5} mb="sm">
              Contact Information
            </Title>
          </Grid.Col>

          <Grid.Col span={12}>
            <TextInput
              label="Contact Person"
              placeholder="Enter contact person name"
              value={formData.contactPerson}
              onChange={(e) =>
                handleInputChange("contactPerson", e.target.value)
              }
              error={getDisplayError("contactPerson")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Phone"
              placeholder="Enter phone number"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              error={getDisplayError("phone")}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              error={getDisplayError("email")}
              type="email"
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <TextInput
              label="Website"
              placeholder="https://example.com"
              value={formData.website}
              onChange={(e) => handleInputChange("website", e.target.value)}
              error={getDisplayError("website")}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Status */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                error={getDisplayError("status")}
                required
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
